import { type WidgetPayload } from '../../utils/api-client/sse-schemas.ts'
import * as styles from './activity-widget.css.ts'

/**
 * One folded widget ready to render. `id` is the widget's `activity_id`, which stays fixed for the widget's
 * lifetime, so it is stable as a React key across successive snapshots. It is required here even though the
 * schema leaves `activity_id` optional: a snapshot arriving without one has no stable fold key and so
 * cannot be updated in place anyway, and the fold drops it rather than inventing an id.
 */
export type ActivityWidgetItem = { id: string; payload: WidgetPayload }

export type ActivityWidgetProps = {
  payload: WidgetPayload
}

/**
 * Renders one activity widget from its structured payload, nested inside its container activity's card.
 *
 * An unrecognized `kind` falls back to the payload's plain-text summary rather than a blank card, so a
 * producer that leads the consumer during a deploy degrades quietly. Payloads are structured data from a
 * closed vocabulary, never agent-authored markup, so nothing here reaches `dangerouslySetInnerHTML`.
 */
export const ActivityWidget: React.FC<ActivityWidgetProps> = ({ payload }) => {
  // The `text` kind and the unknown-kind fallback both render the payload's plain-text summary, so there is
  // one branch today — a ts-pattern match would have two identical arms. A kind carrying its own visual
  // treatment (the dependency graph in ISS-8082) is what introduces the match.
  const { message } = payload

  // `message` is optional on every kind, so a payload carrying only structured fields renders nothing
  // rather than an empty bordered box.
  if (!message) {
    return null
  }

  // Not a live region. Each snapshot replaces the whole accumulated message rather than appending a line,
  // so `role="log"` (which implies `aria-live="polite"`) would re-announce the entire growing transcript on
  // every frame. This is a visual transcript; the step card's own status role carries the announcement.
  return (
    <div className={styles.widget} aria-live="off">
      {message}
    </div>
  )
}
